import { useCallback } from 'react';
import type { Disposable, UseMutationConfig } from 'react-relay';
import type {
	main_updateFieldSetPreferencesMutation as UpdateFieldSetPreferencesMutation,
	main_updateFieldSetPreferencesMutation$data as UpdateFieldSetPreferencesMutationData,
} from '@atlassian/jira-relay/src/__generated__/main_updateFieldSetPreferencesMutation.graphql';
import type {
	main_setFieldSetsPreferencesMutation,
	main_setFieldSetsPreferencesMutation$data,
} from '@atlassian/jira-relay/src/__generated__/main_setFieldSetsPreferencesMutation.graphql';
import { MutationErrorCollection } from '@atlassian/jira-mutation-error/src/mutation-error-collection.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';

type FieldSetPreferenceUpdateType = 'isFrozen' | 'width';

type FieldSetPreferenceNode =
	| { fieldSetId: string; width: number | null }
	| { fieldSetId: string; isFrozen: boolean };

export const useCommitFieldSetPreferences = ({
	cloudId,
	projectId,
	viewId,
	isInlineFieldConfigEnabled,
	updateFieldSetPreferences,
	updateFieldSetPreferencesOld,
}: {
	cloudId: string;
	viewId: string;
	projectId?: string | null;
	isInlineFieldConfigEnabled?: boolean;
	updateFieldSetPreferences: (
		config: UseMutationConfig<main_setFieldSetsPreferencesMutation>,
	) => Disposable;
	updateFieldSetPreferencesOld: (
		config: UseMutationConfig<UpdateFieldSetPreferencesMutation>,
	) => Disposable;
}) => {
	const commitUpdateFieldSetPreferencesOld = useCallback(
		(payload: FieldSetPreferenceNode[], updateType?: FieldSetPreferenceUpdateType) => {
			updateFieldSetPreferencesOld({
				variables: {
					cloudId,
					input: {
						nodes: payload,
					},
					...(isInlineFieldConfigEnabled && {
						context: {
							projectContext: {
								project: projectId,
							},
						},
						viewId,
					}),
				},
				onCompleted(data: UpdateFieldSetPreferencesMutationData) {
					if (!data.jira?.updateUserFieldSetPreferences?.success) {
						fireErrorAnalytics({
							meta: {
								id: 'commitUpdateFieldSetPreferencesMutationUnsuccessful',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							sendToPrivacyUnsafeSplunk: true,
							...(updateType ? { attributes: { updateType } } : {}),
						});
					}
				},
				onError(error: Error) {
					fireErrorAnalytics({
						meta: {
							id: 'commitUpdateFieldSetPreferencesMutationError',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						...(updateType ? { attributes: { updateType } } : {}),
					});
				},
			});
		},
		[cloudId, isInlineFieldConfigEnabled, projectId, updateFieldSetPreferencesOld, viewId],
	);

	const commitUpdateFieldSetPreferences = useCallback(
		(
			savedViewId: string,
			payload: FieldSetPreferenceNode[],
			updateType?: FieldSetPreferenceUpdateType,
		) => {
			updateFieldSetPreferences({
				variables: {
					input: {
						viewId: savedViewId,
						fieldSetsPreferences: payload,
					},
				},
				onCompleted(data: main_setFieldSetsPreferencesMutation$data) {
					if (!data.jira_setFieldSetsPreferences?.success) {
						const errors = new MutationErrorCollection(data.jira_setFieldSetsPreferences?.errors);
						// silently fail as column will remain at the desired width until the next page load
						fireErrorAnalytics({
							meta: {
								id: 'commitUpdateFieldSetPreferencesMutationUnsuccessful',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							...(updateType ? { attributes: { updateType } } : {}),
							error: errors,
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					// silently fail as column will remain at the desired width until the next page load
					fireErrorAnalytics({
						meta: {
							id: 'commitUpdateFieldSetPreferencesMutationError',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						...(updateType ? { attributes: { updateType } } : {}),
					});
				},
			});
		},
		[updateFieldSetPreferences],
	);

	return {
		commitUpdateFieldSetPreferences,
		commitUpdateFieldSetPreferencesOld,
	};
};
